import React, { useState, useEffect } from "react";
import "./Orders.css";
import { backend_url, currency } from "../../App";
import * as XLSX from "xlsx";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Sorting states
  const [sortConfig, setSortConfig] = useState({
    deliveryStatus: "none",
    paymentStatus: "none",
  });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${backend_url}/admin/allorders`, {
        headers: {
          "auth-token": localStorage.getItem("auth-token"),
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }

      const data = await response.json();
      if (data.success) {
        setOrders(data.orders);
        setFilteredOrders(data.orders);
      } else {
        throw new Error(data.message || "Failed to fetch orders");
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Sorting function
  const handleSort = (field) => {
    const newSortConfig = { ...sortConfig };

    // Reset other field sorting
    Object.keys(newSortConfig).forEach((key) => {
      if (key !== field) {
        newSortConfig[key] = "none";
      }
    });

    // Toggle sorting order
    if (newSortConfig[field] === "none") {
      newSortConfig[field] = "asc";
    } else if (newSortConfig[field] === "asc") {
      newSortConfig[field] = "desc";
    } else {
      newSortConfig[field] = "none";
    }

    setSortConfig(newSortConfig);

    // Apply sorting
    if (newSortConfig[field] === "none") {
      setFilteredOrders([...orders]);
    } else {
      const sortedOrders = [...orders].sort((a, b) => {
        const aValue = a[field] || "";
        const bValue = b[field] || "";

        if (newSortConfig[field] === "asc") {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      });
      setFilteredOrders(sortedOrders);
    }
  };

  // Excel export function
  const exportToExcel = () => {
    const exportData = filteredOrders.map((order) => ({
      "Order ID": order.orderId,
      "Customer ID": order.customerId,
      "Date Ordered": formatDate(order.dateOrdered),
      "Delivery Status": order.deliveryStatus,
      "Payment Status": order.paymentStatus,
      "Total Amount": `${currency}${order.totalAmount}`,
      "Payment Method": order.paymentMethod,
      "Delivery Date": order.deliveryDate
        ? formatDate(order.deliveryDate)
        : "N/A",
      "Payment Date": order.paymentDate ? formatDate(order.paymentDate) : "N/A",
      "Customer Name": order.shippingAddress.name,
      "Customer Email": order.shippingAddress.email,
      "Customer Phone": order.shippingAddress.phone,
      "Shipping Address": `${order.shippingAddress.address}, ${order.shippingAddress.city}, ${order.shippingAddress.state} - ${order.shippingAddress.pincode}`,
      "Admin Notes": order.adminNotes || "N/A",
      "Items Count": order.items.length,
      Items: order.items
        .map((item) => `${item.name} (${item.size}, Qty: ${item.quantity})`)
        .join("; "),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");

    // Auto-size columns
    const colWidths = [];
    exportData.forEach((row) => {
      Object.keys(row).forEach((key, index) => {
        const length = Math.max(row[key].toString().length, key.length);
        colWidths[index] = Math.max(colWidths[index] || 0, length);
      });
    });

    ws["!cols"] = colWidths.map((width) => ({
      width: Math.min(width + 2, 50),
    }));

    const fileName = `orders_export_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortConfig[field] === "none") return "↕️";
    if (sortConfig[field] === "asc") return "↑";
    if (sortConfig[field] === "desc") return "↓";
    return "↕️";
  };

  const handleStatusUpdate = async (
    orderId,
    deliveryStatus,
    paymentStatus,
    adminNotes
  ) => {
    try {
      setUpdateLoading(true);

      const response = await fetch(`${backend_url}/admin/updateorder`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "auth-token": localStorage.getItem("auth-token"),
        },
        body: JSON.stringify({
          orderId,
          deliveryStatus,
          paymentStatus,
          adminNotes,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update order");
      }

      const data = await response.json();
      if (data.success) {
        alert("Order status updated successfully!");
        fetchOrders(); // Refresh the orders list
        setShowModal(false);
        setSelectedOrder(null);
      } else {
        throw new Error(data.message || "Failed to update order");
      }
    } catch (error) {
      console.error("Error updating order:", error);
      alert("Error updating order: " + error.message);
    } finally {
      setUpdateLoading(false);
    }
  };

  const openUpdateModal = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Delivered":
        return "#28a745";
      case "Shipped":
        return "#17a2b8";
      case "Processing":
        return "#ffc107";
      case "Pending":
        return "#dc3545";
      case "Received":
        return "#28a745";
      default:
        return "#6c757d";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return "/placeholder-image.jpg"; // Return a placeholder if no image path
    }

    // If imagePath already starts with http, return it as is
    if (imagePath.startsWith("http")) {
      return imagePath;
    }

    // Ensure the path starts with /
    if (!imagePath.startsWith("/")) {
      imagePath = "/" + imagePath;
    }

    // Construct the full URL
    return `${backend_url}${imagePath}`;
  };

  if (loading) {
    return (
      <div className="orders-container">
        <div className="loading">Loading orders...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-container">
        <div className="error">Error: {error}</div>
        <button onClick={fetchOrders}>Retry</button>
      </div>
    );
  }

  return (
    <div className="orders-container">
      <div className="orders-header">
        <h1>Order Management</h1>
        <div className="header-actions">
          <button onClick={fetchOrders} className="refresh-btn">
            Refresh Orders
          </button>
          <button onClick={exportToExcel} className="export-btn">
            Export to Excel
          </button>
        </div>
      </div>

      {/* Sorting Controls */}
      <div className="sorting-controls">
        <div className="sort-section">
          <h3>Sort Orders:</h3>
          <div className="sort-buttons">
            <button
              onClick={() => handleSort("deliveryStatus")}
              className={`sort-btn ${
                sortConfig.deliveryStatus !== "none" ? "active" : ""
              }`}
            >
              Delivery Status {getSortIcon("deliveryStatus")}
            </button>
            <button
              onClick={() => handleSort("paymentStatus")}
              className={`sort-btn ${
                sortConfig.paymentStatus !== "none" ? "active" : ""
              }`}
            >
              Payment Status {getSortIcon("paymentStatus")}
            </button>
          </div>
        </div>
        <div className="order-count">
          Showing {filteredOrders.length} of {orders.length} orders
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="no-orders">
          <h2>No orders found</h2>
          <p>Orders placed by customers will appear here.</p>
        </div>
      ) : (
        <div className="orders-list">
          {filteredOrders.map((order) => (
            <div key={order.orderId} className="order-card">
              <div className="order-header">
                <div className="order-info">
                  <h3>Order #{order.orderId}</h3>
                  <p className="order-date">
                    Ordered: {formatDate(order.dateOrdered)}
                  </p>
                  <p className="customer-id">Customer ID: {order.customerId}</p>
                </div>
                <div className="order-status">
                  <div
                    className="status-badge delivery"
                    style={{
                      backgroundColor: getStatusColor(order.deliveryStatus),
                    }}
                  >
                    Delivery: {order.deliveryStatus}
                  </div>
                  <div
                    className="status-badge payment"
                    style={{
                      backgroundColor: getStatusColor(order.paymentStatus),
                    }}
                  >
                    Payment: {order.paymentStatus}
                  </div>
                </div>
              </div>

              <div className="order-details">
                <div className="order-items">
                  <h4>Items ({order.items.length})</h4>
                  {order.items.map((item, index) => (
                    <div key={index} className="order-item">
                      <img
                        src={getImageUrl(item.image)}
                        alt={item.name || "Product image"}
                        onError={(e) => {
                          e.target.src = "/placeholder-image.jpg";
                          e.target.onerror = null; // Prevent infinite loop
                        }}
                        loading="lazy"
                      />
                      <div className="item-details">
                        <p className="item-name">{item.name}</p>
                        <p className="item-info">
                          Size: {item.size} | Qty: {item.quantity} | {currency}
                          {item.price}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="order-summary">
                  <div className="summary-row">
                    <span>Total Amount:</span>
                    <span className="total-amount">
                      {currency}
                      {order.totalAmount}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span>Payment Method:</span>
                    <span>{order.paymentMethod}</span>
                  </div>
                  {order.deliveryDate && (
                    <div className="summary-row">
                      <span>Delivered:</span>
                      <span>{formatDate(order.deliveryDate)}</span>
                    </div>
                  )}
                  {order.paymentDate && (
                    <div className="summary-row">
                      <span>Payment Received:</span>
                      <span>{formatDate(order.paymentDate)}</span>
                    </div>
                  )}
                </div>

                <div className="shipping-address">
                  <h4>Shipping Address</h4>
                  <p>{order.shippingAddress.name}</p>
                  <p>{order.shippingAddress.email}</p>
                  <p>{order.shippingAddress.phone}</p>
                  <p>{order.shippingAddress.address}</p>
                  <p>
                    {order.shippingAddress.city}, {order.shippingAddress.state}{" "}
                    - {order.shippingAddress.pincode}
                  </p>
                </div>

                {order.adminNotes && (
                  <div className="admin-notes">
                    <h4>Admin Notes</h4>
                    <p>{order.adminNotes}</p>
                  </div>
                )}
              </div>

              <div className="order-actions">
                <button
                  onClick={() => openUpdateModal(order)}
                  className="update-btn"
                >
                  Update Status
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Update Status Modal */}
      {showModal && selectedOrder && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Update Order #{selectedOrder.orderId}</h2>
              <button onClick={() => setShowModal(false)} className="close-btn">
                ×
              </button>
            </div>

            <UpdateOrderForm
              order={selectedOrder}
              onUpdate={handleStatusUpdate}
              onCancel={() => setShowModal(false)}
              loading={updateLoading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Update Order Form Component
const UpdateOrderForm = ({ order, onUpdate, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    deliveryStatus: order.deliveryStatus,
    paymentStatus: order.paymentStatus,
    adminNotes: order.adminNotes || "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(
      order.orderId,
      formData.deliveryStatus,
      formData.paymentStatus,
      formData.adminNotes
    );
  };

  return (
    <form onSubmit={handleSubmit} className="update-form">
      <div className="form-group">
        <label>Delivery Status:</label>
        <select
          value={formData.deliveryStatus}
          onChange={(e) =>
            setFormData({ ...formData, deliveryStatus: e.target.value })
          }
        >
          <option value="Pending">Pending</option>
          <option value="Processing">Processing</option>
          <option value="Shipped">Shipped</option>
          <option value="Delivered">Delivered</option>
        </select>
      </div>

      <div className="form-group">
        <label>Payment Status:</label>
        <select
          value={formData.paymentStatus}
          onChange={(e) =>
            setFormData({ ...formData, paymentStatus: e.target.value })
          }
        >
          <option value="Pending">Pending</option>
          <option value="Received">Received</option>
        </select>
      </div>

      <div className="form-group">
        <label>Admin Notes:</label>
        <textarea
          value={formData.adminNotes}
          onChange={(e) =>
            setFormData({ ...formData, adminNotes: e.target.value })
          }
          placeholder="Add any notes for the customer..."
          rows="3"
        />
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
        <button type="submit" disabled={loading}>
          {loading ? "Updating..." : "Update Order"}
        </button>
      </div>
    </form>
  );
};

export default Orders;
